'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { CloseOutlined, MarkEmailRead, Send } from '@mui/icons-material';
import { Box, Button, Collapse, IconButton, Input, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { createMenuStyles } from '@/components/Nav/MenuStyles';

interface FormLendIQSignInProps {
  open: boolean;
  onClose: () => void;
  onSubmitEmail?: (_email: string) => Promise<void> | void;
  anchorEl?: HTMLElement | null;
}

const signInSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});

type SignInFormValues = z.infer<typeof signInSchema>;

export function FormLendIQSignIn({ open, onClose, onSubmitEmail }: FormLendIQSignInProps) {
  const theme = useTheme();
  const styles = createMenuStyles(theme);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '' },
  });

  const emailValue = watch('email');

  useEffect(() => {
    if (open) {
      setError(false);
      setSubmitted(false);
      reset({ email: '' });
    }
  }, [open, reset]);

  const onSubmit = async (values: SignInFormValues) => {
    setIsLoading(true);
    setError(false);
    setErrorMessage('');

    try {
      const response = await fetch(
        `/api/validate-lend-iq-email/?email=${encodeURIComponent(values.email)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // eslint-disable-next-line no-console
        console.log('[LOGIN_CHECK] Data in signIn callback:', data);

        if (data.success !== true) {
          setError(true);
          setErrorMessage(
            'This email is not registered with Lend IQ. \n Please contact support if you believe this is an error.'
          );
          return;
        }

        setSubmitted(true);
        if (onSubmitEmail) {
          await onSubmitEmail(values.email);
        }
      } else {
        try {
          const errorData = await response.json();

          // eslint-disable-next-line no-console
          console.log('[LOGIN_CHECK] Error data:', errorData);

          if (errorData.error) {
            console.error('API Error:', errorData.error);
            if (errorData.error === 'Invalid email') {
              setErrorMessage(
                `This email is not registered with Lend IQ.
                Please contact support if you believe this is an error.`
              );
            } else {
              setErrorMessage(errorData.error);
            }
          } else {
            setErrorMessage('Failed to send login link. PPlease try again.');
          }
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          setErrorMessage('Failed to send login link. Please try again.');
        }
        setError(true);
        console.error('Login request failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error in signIn callback:', error);
      setError(true);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Collapse
      in={open}
      timeout={300}
      sx={{
        position: 'absolute',
        top: 'calc(100% + 0.5rem)',
        right: 0,
        zIndex: 1000,
        transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&.MuiCollapse-entering': {
          opacity: 0,
        },
        '&.MuiCollapse-entered': {
          opacity: 1,
        },
        '&.MuiCollapse-exiting': {
          opacity: 0,
        },
      }}
    >
      <Box
        data-component="FormLendIQSignIn"
        sx={{
          ...styles.dropdown.paperFirstLevel,
          maxHeight: 'calc(100vh - 6rem)',
          overflowY: 'auto',
          width: 320,
          p: 2.5,
          py: 1.5,
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: -8, right: -8, zIndex: 50 }}>
            <IconButton onClick={onClose} aria-label="Close sign in" size="small">
              <CloseOutlined />
            </IconButton>
          </Box>
          <Typography
            component={'strong'}
            sx={{
              pr: 3,
              fontWeight: 'bold',
              color: theme.palette.common.black,
            }}
          >
            Sign in to Lend IQ
          </Typography>
          {!submitted ? (
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ display: 'grid', gap: 0.5 }}
            >
              <Typography
                variant="small"
                sx={{
                  mt: 1.5,
                  color: theme.palette.common.black,
                }}
              >
                Enter your email and we'll send you a login link
              </Typography>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="email"
                    onChange={(e) => {
                      if (error) {
                        setError(false);
                      }
                      field.onChange(e);
                    }}
                    error={!!errors.email}
                    fullWidth
                    size="small"
                    sx={{
                      color: theme.palette.common.black,
                      backgroundColor: theme.palette.common.white,
                      border: `1px solid ${theme.palette.purple[500]}`,
                      py: 1,
                      px: 1.5,
                      borderRadius: 2,
                    }}
                  />
                )}
              />

              <Button
                type="submit"
                variant="contained"
                endIcon={<Send />}
                disabled={!emailValue || isLoading || !!errors.email}
                sx={{
                  mt: 1,
                  px: 3,
                  py: { xs: 1, sm: 1.5 },
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  backgroundColor: theme.palette.brand.primary,
                  color: 'white',
                  fontSize: '1.15rem',
                  lineHeight: 1,
                  '& svg': {
                    height: '1.6rem',
                    width: '1.6rem',
                    ml: '0.25rem',
                  },
                  '&:hover': { backgroundColor: theme.palette.brand.dark },
                  '&:disabled': {
                    backgroundColor: theme.palette.grey[400],
                    color: theme.palette.grey[600],
                  },
                }}
              >
                {isLoading ? 'Sending...' : 'Send my secure login link'}
              </Button>

              {error && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                    mt: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {errorMessage || 'Failed to send login link. Please try again.'}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 4,
                p: 2.5,
                py: 1.5,
                mt: 2,
                alignItems: 'center',
                color: 'brand.green',
                border: `0.2rem solid ${theme.palette.brand.green}`,
                borderRadius: 2,
                svg: {
                  width: '2.5rem',
                  height: '2.5rem',
                },
              }}
            >
              <MarkEmailRead width={'3rem'} />
              <Typography variant="body2" color="common.black">
                Check your email for the sign-in link.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Collapse>
  );
}
